import React, { useEffect, useMemo, useRef, useState } from "react";
import { getMemberById } from "../../api/productApi";

export default function SearchableMemberSelect({
    members = [],
    value = "",
    onChange = () => { },
    placeholder = "Search member name / id...",
}) {
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState("");
    const [extraMembers, setExtraMembers] = useState([]);
    const [loading, setLoading] = useState(false);

    const wrapRef = useRef(null);

    // close on outside click
    useEffect(() => {
        function handleClickOutside(e) {
            if (!wrapRef.current) return;
            if (!wrapRef.current.contains(e.target)) setOpen(false);
        }

        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    // Combine standard members and dynamically loaded members
    const allMembers = useMemo(() => {
        const combined = [...members];
        extraMembers.forEach(extra => {
            if (!combined.some(m => String(m.id) === String(extra.id))) {
                combined.push(extra);
            }
        });
        return combined;
    }, [members, extraMembers]);

    // Debug logs for loaded members
    useEffect(() => {
        console.log("[SearchableMemberSelect] Members loaded into dropdown:", allMembers);
    }, [allMembers]);

    const selectedMember = useMemo(() => {
        return allMembers.find((m) => String(m.id) === String(value)) || null;
    }, [allMembers, value]);

    // when value changes from outside, show selected label
    useEffect(() => {
        if (selectedMember) {
            setQuery(`${selectedMember.name} (ID: ${selectedMember.memberId})`);
        } else {
            setQuery("");
        }
    }, [selectedMember]);

    // Debug logging for query input
    useEffect(() => {
        console.log("[SearchableMemberSelect] Search term:", query);
    }, [query]);

    // Filter results locally
    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();
        if (!q) return allMembers.slice(0, 10);

        const resList = allMembers.filter((m) => {
            const name = String(m.name || "").toLowerCase();
            const id = String(m.memberId || m.id || "").toLowerCase();
            const phone = String(m.phone || "").toLowerCase();
            const email = String(m.email || "").toLowerCase();

            return (
                name.includes(q) ||
                id.includes(q) ||
                phone.includes(q) ||
                email.includes(q)
            );
        });

        // Debug check to see if searched ID exists in allMembers but gets excluded by filtering
        if (/^\d+$/.test(q)) {
            const existsInAll = allMembers.some(m => String(m.id) === q || String(m.memberId) === q);
            const existsInFiltered = resList.some(m => String(m.id) === q || String(m.memberId) === q);
            if (existsInAll && !existsInFiltered) {
                console.warn(`[SearchableMemberSelect] Member ${q} exists in state but is EXCLUDED by filtering logic!`);
            }
        }

        console.log("[SearchableMemberSelect] Filtered results:", resList);
        return resList.slice(0, 10);
    }, [allMembers, query]);

    // Automatically query backend if numeric ID is typed but not present in allMembers
    useEffect(() => {
        const idQuery = query.trim();
        if (!/^\d+$/.test(idQuery)) return;

        const alreadyPresent = allMembers.some(
            (m) => String(m.id) === idQuery || String(m.memberId) === idQuery
        );
        if (alreadyPresent) return;

        const token = localStorage.getItem("token") || sessionStorage.getItem("token");
        if (!token) return;

        setLoading(true);
        console.log("[SearchableMemberSelect] Triggering background API lookup for missing ID:", idQuery);
        const delayDebounceFn = setTimeout(async () => {
            try {
                const res = await getMemberById({ token, userId: idQuery });
                console.log("[SearchableMemberSelect] API response for member lookup:", res);
                if (res && res.success && res.data && res.data.member_information) {
                    const info = res.data.member_information;
                    const newMember = {
                        id: Number(idQuery),
                        name: info.member_name || "Unknown",
                        memberId: Number(idQuery),
                        email: info.email || "",
                        phone: info.phone_number || info.phone || "",
                        address: info.billing_address || info.address || "",
                    };
                    console.log("[SearchableMemberSelect] Adding looked-up member to state:", newMember);
                    setExtraMembers((prev) => {
                        if (prev.some((x) => String(x.id) === String(newMember.id))) {
                            return prev;
                        }
                        return [...prev, newMember];
                    });
                }
            } catch (err) {
                console.error("[SearchableMemberSelect] Auto lookup error:", err);
            } finally {
                setLoading(false);
            }
        }, 350);

        return () => clearTimeout(delayDebounceFn);
    }, [query, allMembers]);

    function handleSelect(m) {
        onChange(String(m.id), m);
        setOpen(false);
    }

    return (
        <div ref={wrapRef} className="relative w-full">
            <input
                value={query}
                onFocus={() => setOpen(true)}
                onChange={(e) => {
                    setQuery(e.target.value);
                    setOpen(true);
                    onChange("");
                }}
                placeholder={placeholder}
                className="w-full border rounded-md px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-black/10"
            />

            {open && (
                <div className="absolute z-50 mt-2 w-full bg-white border rounded-md shadow-lg max-h-60 overflow-y-auto">
                    <button
                        onClick={() => {
                            onChange("");
                            setQuery("");
                            setOpen(false);
                        }}
                        className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 text-gray-500"
                    >
                        None
                    </button>

                    {filtered.length === 0 && (
                        <div className="px-3 py-2 text-sm text-gray-400">
                            {loading ? "Searching..." : "No member found"}
                        </div>
                    )}

                    {filtered.map((m) => (
                        <button
                            key={m.id}
                            onClick={() => handleSelect(m)}
                            className="w-full text-left px-3 py-2 hover:bg-gray-50"
                        >
                            <div className="text-sm font-medium text-gray-800">
                                {m.name || "-"}
                            </div>
                            <div className="text-xs text-gray-400">
                                ID: {m.memberId} {m.phone ? `• ${m.phone}` : ""}{" "}
                                {m.email ? `• ${m.email}` : ""}
                            </div>
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
}


